import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SPMappingDialog = ({ open, onClose, unmappedAccounts, chartType, onSaved }) => {
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch categories
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !open) return;
    fetch(`${API}/api/sp-mappings/categories`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => setCategories(data.categories || []))
      .catch(() => {});
  }, [open]);

  // Initialize mappings with suggestions
  useEffect(() => {
    if (!unmappedAccounts?.length) return;
    const initial = {};
    unmappedAccounts.forEach(acc => {
      initial[acc.account_code] = acc.suggested_category || '';
    });
    setMappings(initial);
  }, [unmappedAccounts]);

  const handleSave = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    try {
      const payload = Object.entries(mappings).map(([code, cat]) => {
        const acc = unmappedAccounts.find(a => a.account_code === code);
        return {
          account_code: code,
          account_name: acc?.account_name || '',
          sp_category: cat,
        };
      }).filter(m => m.sp_category);

      const resp = await fetch(`${API}/api/sp-mappings/${chartType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        const result = await resp.json();
        toast.success(`${result.saved} mappature SP salvate`);
        onSaved?.();
        onClose();
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    }
    setSaving(false);
  }, [mappings, unmappedAccounts, chartType, onSaved, onClose]);

  // Group categories by section for the dropdown
  const categoryGroups = React.useMemo(() => {
    const groups = {};
    categories.forEach(c => {
      const section = c.section;
      if (!groups[section]) groups[section] = [];
      groups[section].push(c);
    });
    return groups;
  }, [categories]);

  const sectionLabels = {
    'attivo_fisso': 'Attivo Fisso',
    'attivo_corrente': 'Attivo Corrente',
    'ratei_attivi': 'Ratei Attivi',
    'mezzi_propri': 'Mezzi Propri',
    'passivo_corrente': 'Passivo Corrente',
    'passivo_consolidato': 'Passivo Consolidato',
    'ratei_passivi': 'Ratei Passivi',
  };

  if (!unmappedAccounts?.length) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sp-mapping-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Landmark className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <DialogTitle>Configurazione Conti SP</DialogTitle>
              <DialogDescription>
                Piano: <Badge variant="outline" className="ml-1">{chartType}</Badge> &mdash;
                {unmappedAccounts.length} conti da abbinare.
                Queste mappature saranno usate per tutti i clienti con piano {chartType}.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2" style={{ maxHeight: '55vh' }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-2 px-2 font-semibold text-slate-600 w-[140px]">Codice</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-600">Descrizione</th>
                <th className="text-left py-2 px-2 font-semibold text-slate-600 w-[250px]">Categoria SP</th>
              </tr>
            </thead>
            <tbody>
              {unmappedAccounts.map((acc) => (
                <tr key={acc.account_code} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-1.5 px-2 font-mono text-xs text-slate-500">{acc.account_code}</td>
                  <td className="py-1.5 px-2 text-slate-700">{acc.account_name}</td>
                  <td className="py-1.5 px-2">
                    <Select
                      value={mappings[acc.account_code] || ''}
                      onValueChange={(val) => setMappings(prev => ({ ...prev, [acc.account_code]: val }))}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`sp-map-${acc.account_code}`}>
                        <SelectValue placeholder="Seleziona categoria..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryGroups).map(([section, cats]) => (
                          <React.Fragment key={section}>
                            <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50">
                              {sectionLabels[section] || section}
                            </div>
                            {cats.map(c => (
                              <SelectItem key={c.key} value={c.key} className="text-xs">
                                {c.label}
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="flex items-center justify-between pt-3 border-t">
          <p className="text-xs text-muted-foreground">
            Queste mappature saranno usate automaticamente per tutti i futuri import {chartType}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="sp-mapping-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Salva Mappature
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SPMappingDialog;
